import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Stack, Typography, Modal, styled, Box } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { DropZone } from "@/components/elements/DropZone";
import { Button } from "@/components/elements/Button";
import { Progress } from "@/components/elements/Progress";
import { useState } from "react";
import { TExtendFile } from "@/types/file";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IconClose } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";

type TImportPartnerUserModalProps = {
  modal: UseVisibleReturn;
  isLoading?: boolean;
};

export const ImportPartnerUserModal: FCC<TImportPartnerUserModalProps> = ({ modal, isLoading }) => {
  const [progress, setProgress] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<ArrayBuffer | null>();
  const [errorList, setErrorList] = useState<string[]>([]);
  const isDisabledSubmitButton = !file || isSubmit;

  const onClose = () => {
    setProgress(0);
    setIsSubmit(false);
    setFile(null);
    setErrorList([]);
    modal.close();
  };

  const downloadSample = () => {};

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    if (!files[0]) return;
    const data = await files[0].arrayBuffer();
    setFile(data);
    setErrorList([]);
  };

  const handleImportFile = async () => {};

  return (
    <Modal
      open={modal.visible}
      hideBackdrop={false}
      sx={{
        position: "fixed",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer width={920}>
        <Stack
          bgcolor={background.disable}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height="53px"
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            padding: "16px",
          }}
        >
          <Typography variant="body14" color={text.primary}>
            企業情報 | 一括インポート
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Stack
          sx={{
            padding: "32px 16px ",
            gap: "24px",
          }}
        >
          {!isSubmit && (
            <Box borderColor={divider.middle} borderRadius={rounded.sm}>
              {errorList.length > 0 && (
                <Typography variant="body14" color="#d32f2f">
                  {errorList.map((error, idx) => (
                    <p key={idx}>{error}</p>
                  ))}
                </Typography>
              )}
              <DropZone
                options={{ multiple: false }}
                onChange={handleChangeFiles}
                rules={{
                  acceptMimeTypes: ["text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
                }}
              >
                <Typography variant="body14" whiteSpace="pre-wrap" textAlign="center">
                  アップロードするファイル（csv、xlsx、xlsm）をドラッグ&ドロップ
                </Typography>
                <Typography variant="body14" width={920} textAlign="center">
                  または
                </Typography>
                <Button type="button" variant="outline">
                  ファイルを選択する
                </Button>
              </DropZone>
            </Box>
          )}
          {isSubmit && (
            <Stack height={150} justifyItems="center" justifyContent="center">
              <Progress variant="determinate" value={progress} />
              <Typography variant="body14" textAlign="center">
                処理中
              </Typography>
            </Stack>
          )}
        </Stack>
        <hr />
        <Stack
          flexDirection="row"
          gap="16px"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: "16px 32px",
          }}
        >
          <Stack flexDirection="row" gap="16px" justifyContent="flex-start">
            <Button variant="secondary" onClick={onClose}>
              キャンセル
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-end">
            {!isSubmit && (
              <Button variant="outline" onClick={() => downloadSample()} startIcon={<FileDownloadIcon />}>
                テンプレートをダウンロード
              </Button>
            )}
            <Button isLoading={isLoading} disabled={isDisabledSubmitButton} onClick={handleImportFile} startIcon={<FileUploadIcon />}>
              {isSubmit ? "ファイルを処理中..." : "アップロード"}
            </Button>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  min-width: 417px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none; /* Remove default focus outline */

  &:focus {
    outline: none;
  }
`;
